import { useEffect, useRef } from 'react';
import { gsap, ScrollTrigger } from '@/hooks/useGsapAnimations';

gsap.registerPlugin(ScrollTrigger);

const AboutUs = () => {
    const sectionRef = useRef<HTMLElement>(null);

    useEffect(() => {
        const el = sectionRef.current;
        if (!el) return;

        const ctx = gsap.context(() => {
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: el,
                    start: 'top 80%',
                    toggleActions: 'play none none none',
                },
            });

            // Left illustration slides in from left
            tl.fromTo('.about-illustration', { opacity: 0, x: -60 }, { opacity: 1, x: 0, duration: 0.8, ease: 'power2.out' });

            // Right text content staggers in from right
            tl.fromTo('.about-title', { opacity: 0, x: 40 }, { opacity: 1, x: 0, duration: 0.6, ease: 'power2.out' }, '-=0.5');

            tl.fromTo(
                '.about-paragraph',
                { opacity: 0, x: 30 },
                { opacity: 1, x: 0, duration: 0.5, ease: 'power2.out', stagger: 0.15 },
                '-=0.3'
            );

            // Buttons fade up
            tl.fromTo(
                '.about-btn',
                { opacity: 0, y: 20 },
                { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out', stagger: 0.15 },
                '-=0.2'
            );
        }, el);

        return () => ctx.revert();
    }, []);

    return (
        <section ref={sectionRef} id="about-us" className="relative z-10 px-4 sm:px-6 lg:px-20 py-20 md:py-28 max-w-[1440px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16 items-center">
                {/* Left Side - Illustration */}
                <div className="about-illustration flex items-center justify-center order-1 lg:order-1" style={{ opacity: 0 }}>
                    <div className="relative w-full max-w-[500px]">
                        {/* Main illustration container */}
                        <div className="relative aspect-square">
                            {/* Glow effect */}
                            <div className="absolute inset-0 bg-[#8E57FA]/20 blur-[100px] rounded-full" />

                            {/* Illustration placeholder */}
                            <div className="relative z-90 w-full h-full flex items-center justify-center">
                                <img
                                    src="/AboutUs.png"
                                    alt="About Us Team"
                                    className="w-full h-full object-contain drop-shadow-2xl"
                                    onError={() => {
                                        console.error("AboutUs.png not found");
                                    }}
                                />


                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side - Content */}
                <div className="space-y-6 sm:space-y-8 order-2 lg:order-2">
                    {/* Title */}
                    <h2
                        className="about-title text-white"
                        style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontWeight: 500,
                            fontSize: "clamp(32px, 7vw, 56px)",
                            lineHeight: "1.2",
                            letterSpacing: "0px",
                            opacity: 0,
                        }}
                    >
                        About Us
                    </h2>

                    {/* Description Paragraphs */}
                    <div className="space-y-4 sm:space-y-[8px]">
                        <p
                            className="about-paragraph text-[#DCCBFD]"
                            style={{
                                fontFamily: "'Roboto', sans-serif",
                                fontWeight: 400,
                                fontSize: "clamp(14px, 3.5vw, 16px)",
                                lineHeight: "clamp(24px, 5.5vw, 28px)",
                                letterSpacing: "0.3px",
                                opacity: 0,
                            }}
                        >
                            Founded by Wellcrafted technologies, Eddy AI was born from a simple belief that every student deserves access to personalized, high-quality learning tools.
                        </p>

                        <p
                            className="about-paragraph text-[#DCCBFD]"
                            style={{
                                fontFamily: "'Roboto', sans-serif",
                                fontWeight: 400,
                                fontSize: "clamp(14px, 3.5vw, 16px)",
                                lineHeight: "clamp(24px, 5.5vw, 28px)",
                                letterSpacing: "0.3px",
                                opacity: 0,
                            }}
                        >
                            We combine cutting-edge technology with proven pedagogical methods to create an adaptive learning experience that meets each student exactly where they are.
                        </p>

                        <p
                            className="about-paragraph text-[#DCCBFD]"
                            style={{
                                fontFamily: "'Roboto', sans-serif",
                                fontWeight: 400,
                                fontSize: "clamp(14px, 3.5vw, 16px)",
                                lineHeight: "clamp(24px, 5.5vw, 28px)",
                                letterSpacing: "0.3px",
                                opacity: 0,
                            }}
                        >
                            Today, students across different countries trust Eddy AI to help them achieve their academic goals and unlock their full potential.
                        </p>
                    </div>

                    {/* CTA Buttons */}
                    <div className="flex flex-wrap gap-4 sm:gap-5 pt-4">
                        <a
                            href="https://www.wellcraftedtech.com/"
                            className="about-btn group hover:scale-105 cursor-pointer transition-all active:scale-95 drop-shadow-xl"
                            style={{ opacity: 0 }}
                        >
                            {/* Inlined from /VisitUs.svg so the arrow can be animated on hover */}
                            <svg
                                viewBox="0 0 136 48"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                role="img"
                                aria-label="Visit Us"
                                className="h-[40px] sm:h-[44px] md:h-[48px] w-auto"
                            >
                                <rect width="135.627" height="48" rx="12" fill="#6B41BC" />
                                <path d="M35.136 18.832L31.04 30H28.32L24.224 18.832H26.624L29.696 27.712L32.752 18.832H35.136ZM37.775 20.08C37.3803 20.08 37.0497 19.9573 36.783 19.712C36.527 19.456 36.399 19.1413 36.399 18.768C36.399 18.3947 36.527 18.0853 36.783 17.84C37.0497 17.584 37.3803 17.456 37.775 17.456C38.1697 17.456 38.495 17.584 38.751 17.84C39.0177 18.0853 39.151 18.3947 39.151 18.768C39.151 19.1413 39.0177 19.456 38.751 19.712C38.495 19.9573 38.1697 20.08 37.775 20.08ZM38.879 21.136V30H36.639V21.136H38.879ZM44.6441 30.144C43.9188 30.144 43.2681 30.016 42.6921 29.76C42.1161 29.4933 41.6575 29.136 41.3161 28.688C40.9855 28.24 40.8041 27.744 40.7721 27.2H43.0281C43.0708 27.5413 43.2361 27.824 43.5241 28.048C43.8228 28.272 44.1908 28.384 44.6281 28.384C45.0548 28.384 45.3855 28.2987 45.6201 28.128C45.8655 27.9573 45.9881 27.7387 45.9881 27.472C45.9881 27.184 45.8388 26.9707 45.5401 26.832C45.2521 26.6827 44.7881 26.5227 44.1481 26.352C43.4868 26.192 42.9428 26.0267 42.5161 25.856C42.1001 25.6853 41.7375 25.424 41.4281 25.072C41.1295 24.72 40.9801 24.2453 40.9801 23.648C40.9801 23.1573 41.1188 22.7093 41.3961 22.304C41.6841 21.8987 42.0895 21.5787 42.6121 21.344C43.1455 21.1093 43.7695 20.992 44.4841 20.992C45.5401 20.992 46.3828 21.2587 47.0121 21.792C47.6415 22.3147 47.9881 23.024 48.0521 23.92H45.9081C45.8761 23.568 45.7268 23.2907 45.4601 23.088C45.2041 22.8747 44.8575 22.768 44.4201 22.768C44.0148 22.768 43.7001 22.8427 43.4761 22.992C43.2628 23.1413 43.1561 23.3493 43.1561 23.616C43.1561 23.9147 43.3055 24.144 43.6041 24.304C43.9028 24.4533 44.3668 24.608 44.9961 24.768C45.6361 24.928 46.1641 25.0933 46.5801 25.264C46.9961 25.4347 47.3535 25.7013 47.6521 26.064C47.9615 26.416 48.1215 26.8853 48.1321 27.472C48.1321 27.984 47.9881 28.4427 47.7001 28.848C47.4228 29.2533 47.0175 29.5733 46.4841 29.808C45.9615 30.032 45.3481 30.144 44.6441 30.144ZM51.2669 20.08C50.8722 20.08 50.5415 19.9573 50.2749 19.712C50.0189 19.456 49.8909 19.1413 49.8909 18.768C49.8909 18.3947 50.0189 18.0853 50.2749 17.84C50.5415 17.584 50.8722 17.456 51.2669 17.456C51.6615 17.456 51.9869 17.584 52.2429 17.84C52.5095 18.0853 52.6429 18.3947 52.6429 18.768C52.6429 19.1413 52.5095 19.456 52.2429 19.712C51.9869 19.9573 51.6615 20.08 51.2669 20.08ZM52.3709 21.136V30H50.1309V21.136H52.3709ZM57.352 22.976V27.264C57.352 27.5627 57.4213 27.7813 57.56 27.92C57.7093 28.048 57.9547 28.112 58.296 28.112H59.336V30H57.928C56.04 30 55.096 29.0827 55.096 27.248V22.976H54.04V21.136H55.096V18.944H57.352V21.136H59.336V22.976H57.352ZM67.2876 18.832V25.744C67.2876 26.5013 67.485 27.0827 67.8796 27.488C68.2743 27.8827 68.829 28.08 69.5436 28.08C70.269 28.08 70.829 27.8827 71.2236 27.488C71.6183 27.0827 71.8156 26.5013 71.8156 25.744V18.832H74.0716V25.728C74.0716 26.6773 73.8636 27.4827 73.4476 28.144C73.0423 28.7947 72.493 29.2853 71.7996 29.616C71.117 29.9467 70.3543 30.112 69.5116 30.112C68.6796 30.112 67.9223 29.9467 67.2396 29.616C66.5676 29.2853 66.0343 28.7947 65.6396 28.144C65.245 27.4827 65.0476 26.6773 65.0476 25.728V18.832H67.2876ZM79.7879 30.144C79.0625 30.144 78.4119 30.016 77.8359 29.76C77.2599 29.4933 76.8012 29.136 76.4599 28.688C76.1292 28.24 75.9479 27.744 75.9159 27.2H78.1719C78.2145 27.5413 78.3799 27.824 78.6679 28.048C78.9665 28.272 79.3345 28.384 79.7719 28.384C80.1985 28.384 80.5292 28.2987 80.7639 28.128C81.0092 27.9573 81.1319 27.7387 81.1319 27.472C81.1319 27.184 80.9825 26.9707 80.6839 26.832C80.3959 26.6827 79.9319 26.5227 79.2919 26.352C78.6305 26.192 78.0865 26.0267 77.6599 25.856C77.2439 25.6853 76.8812 25.424 76.5719 25.072C76.2732 24.72 76.1239 24.2453 76.1239 23.648C76.1239 23.1573 76.2625 22.7093 76.5399 22.304C76.8279 21.8987 77.2332 21.5787 77.7559 21.344C78.2892 21.1093 78.9132 20.992 79.6279 20.992C80.6839 20.992 81.5265 21.2587 82.1559 21.792C82.7852 22.3147 83.1319 23.024 83.1959 23.92H81.0519C81.0199 23.568 80.8705 23.2907 80.6039 23.088C80.3479 22.8747 80.0012 22.768 79.5639 22.768C79.1585 22.768 78.8439 22.8427 78.6199 22.992C78.4065 23.1413 78.2999 23.3493 78.2999 23.616C78.2999 23.9147 78.4492 24.144 78.7479 24.304C79.0465 24.4533 79.5105 24.608 80.1399 24.768C80.7799 24.928 81.3079 25.0933 81.7239 25.264C82.1399 25.4347 82.4972 25.7013 82.7959 26.064C83.1052 26.416 83.2652 26.8853 83.2759 27.472C83.2759 27.984 83.1319 28.4427 82.8439 28.848C82.5665 29.2533 82.1612 29.5733 81.6279 29.808C81.1052 30.032 80.4919 30.144 79.7879 30.144Z" fill="white" />

                                {/* Arrow — nudges along its own diagonal on hover */}
                                <g className="transition-transform duration-300 ease-out motion-safe:group-hover:translate-x-[2px] motion-safe:group-hover:-translate-y-[2px]">
                                    <path d="M97.0137 27.2998L103.613 20.7001" stroke="white" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M97.0137 20.7002H103.613V27.2999" stroke="white" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                </g>
                            </svg>
                        </a>
                        <a
                            href="mailto:contact@wellcraftedtech.com"
                            className="about-btn hover:scale-105 cursor-pointer transition-all active:scale-95 drop-shadow-xl"
                            style={{ opacity: 0 }}
                        >
                            <img
                                src="/ContactUs.svg"
                                alt="Play Store"
                                className="h-[40px] sm:h-[44px] md:h-[48px] w-auto"
                            />
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default AboutUs;
